import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import {
  AlertTriangle, Plus, ArrowLeft, ArrowRight, CheckCircle2, Clock, Sparkles,
  Shield, ClipboardList, FileText, User, Lock, Eye, EyeOff, TrendingUp, Activity, Printer,
  Link2, Trash2, Search, X,
} from 'lucide-react';
import Header from '../components/Header';
import { SECTIONS } from '../lib/sectionTheme';
import { useAuth } from '../context/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const C = SECTIONS.compliance;

const CATEGORIES = [
  { value: 'near_miss',        label: 'Near miss' },
  { value: 'patient_safety',   label: 'Patient safety event' },
  { value: 'complaint',        label: 'Patient or staff complaint' },
  { value: 'equipment',        label: 'Equipment failure / malfunction' },
  { value: 'staff_injury',     label: 'Staff injury / WHS event' },
  { value: 'infection_breach', label: 'Infection control breach' },
  { value: 'medication',       label: 'Medication / contrast incident' },
  { value: 'radiation',        label: 'Radiation / dose incident' },
  { value: 'communication',    label: 'Communication / handover failure' },
  { value: 'it_system',        label: 'IT / RIS / PACS issue' },
  { value: 'other',            label: 'Other' },
];
const SEVERITIES = [
  { value: 'near_miss', label: 'Near miss — no harm', color: '#16A34A' },
  { value: 'minor',     label: 'Minor — minimal / short-term impact', color: '#F59E0B' },
  { value: 'moderate',  label: 'Moderate — temporary harm / disruption', color: '#D97706' },
  { value: 'major',     label: 'Major — significant harm or systemic risk', color: '#DC2626' },
];

const STATUS_META = {
  reported:         { label: 'Reported', color: '#F59E0B' },
  under_review:     { label: 'Under review', color: '#2563EB' },
  rca_in_progress:  { label: 'RCA in progress', color: '#7C3AED' },
  closed:           { label: 'Closed', color: '#16A34A' },
};

const catLabel = (v) => CATEGORIES.find(c => c.value === v)?.label || v;
const sevMeta = (v) => SEVERITIES.find(s => s.value === v) || { label: v, color: '#64748B' };

// ────────────────────────────────────────────────────────────────────────────
// Index — list of my incidents + "Report incident" CTA + PM dashboard link
// ────────────────────────────────────────────────────────────────────────────
export function IncidentsIndexPage() {
  const { user } = useAuth();
  const isPM = user?.role === 'admin' || user?.role === 'superuser';
  const [mine, setMine] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/incidents?mine=true`, { withCredentials: true })
      .then(r => setMine(r.data))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="min-h-screen bg-background" data-testid="incidents-index">
      <Header />
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <div className="flex items-start justify-between gap-4 flex-wrap mb-8">
          <div>
            <div className="flex items-center gap-2 mb-2">
              <Sparkles className="w-4 h-4" style={{ color: C.color }} />
              <span className="text-xs font-bold uppercase tracking-[0.18em]" style={{ color: C.color }}>Incident reporting</span>
            </div>
            <h1 className="font-['Outfit'] text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-foreground">
              Report an <span style={{ color: C.color }}>incident</span>
            </h1>
            <p className="mt-3 text-sm sm:text-base text-muted-foreground max-w-2xl">
              Any event that is unintended, unexpected, or could have caused harm — including <strong>near misses</strong>. Non-punitive. Reporting is how we learn.
            </p>
          </div>
          <Link to="/incidents/new" data-testid="incidents-new-cta"
            className="inline-flex items-center gap-1.5 px-5 py-3 text-sm font-semibold text-white rounded-xl shrink-0"
            style={{ backgroundColor: C.color }}>
            <Plus className="w-4 h-4" /> New incident report
          </Link>
        </div>

        {/* Safety reassurance card */}
        <div className="p-5 rounded-2xl border-2 border-dashed mb-8" style={{ borderColor: `${C.color}40`, backgroundColor: `${C.color}08` }} data-testid="incidents-safety-note">
          <div className="flex items-start gap-3">
            <Shield className="w-5 h-5 mt-0.5 shrink-0" style={{ color: C.color }} />
            <div>
              <p className="text-sm font-semibold text-foreground">Speak-up culture</p>
              <p className="text-xs text-muted-foreground mt-1">
                Reporting is encouraged and protected. Staff will <strong>not</strong> be penalised for reporting incidents or raising safety concerns. You may submit anonymously if that helps you speak up — the report will still be reviewed.
              </p>
              <Link to="/incidents/culture-poster" data-testid="incidents-poster-link"
                className="inline-flex items-center gap-1 text-xs font-semibold mt-2" style={{ color: C.color }}>
                Print the culture poster <ArrowRight className="w-3 h-3" />
              </Link>
            </div>
          </div>
        </div>

        {/* Admin shortcut */}
        {isPM && (
          <Link to="/management" data-testid="incidents-admin-link"
            className="flex items-center justify-between p-4 bg-card border border-border rounded-xl mb-8 hover:border-[--c]"
            style={{ '--c': C.color }}>
            <div>
              <p className="text-sm font-semibold text-foreground">Incident dashboard (admin view)</p>
              <p className="text-[11px] text-muted-foreground">Review, classify, RCA and close incidents from /management.</p>
            </div>
            <ArrowRight className="w-4 h-4 text-muted-foreground" />
          </Link>
        )}

        {/* My submitted incidents */}
        <h2 className="font-['Outfit'] text-lg font-semibold text-foreground mb-3">My submitted incidents</h2>
        {loading ? (
          <p className="p-6 text-center text-sm text-muted-foreground">Loading…</p>
        ) : mine.length === 0 ? (
          <div className="p-8 text-center border border-dashed border-border rounded-xl" data-testid="incidents-mine-empty">
            <p className="text-sm text-muted-foreground">You haven't filed any incident reports yet.</p>
          </div>
        ) : (
          <ul className="space-y-2" data-testid="incidents-mine-list">
            {mine.map(i => {
              const sv = sevMeta(i.severity);
              const st = STATUS_META[i.status] || STATUS_META.reported;
              return (
                <Link key={i.incident_id} to={`/incidents/${i.incident_id}`}
                  data-testid={`incidents-mine-row-${i.incident_id}`}
                  className="block p-4 bg-card border border-border rounded-xl hover:border-[--c]" style={{ '--c': C.color }}>
                  <div className="flex items-center justify-between gap-3 flex-wrap">
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-foreground truncate">{catLabel(i.category)} · {i.location || '—'}</p>
                      <p className="text-[11px] text-muted-foreground truncate">{new Date(i.submitted_at).toLocaleString()}</p>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full" style={{ backgroundColor: `${sv.color}15`, color: sv.color }}>{sv.label.split(' — ')[0]}</span>
                      <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full" style={{ backgroundColor: `${st.color}15`, color: st.color }}>{st.label}</span>
                    </div>
                  </div>
                </Link>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// Report form
// ────────────────────────────────────────────────────────────────────────────
export function IncidentReportPage() {
  const navigate = useNavigate();
  const nowLocal = () => {
    const d = new Date(); const pad = (n) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  };
  const [form, setForm] = useState({
    category: 'near_miss',
    severity: 'near_miss',
    occurred_at: nowLocal(),
    location: '',
    description: '',
    immediate_actions: '',
    witnesses: '',
    reported_to: '',
    patient: { name: '', dob: '', ur: '' },
    anonymous: false,
  });
  const [includesPatient, setIncludesPatient] = useState(false);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');
  const [done, setDone] = useState(null);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const setPatient = (k, v) => setForm(f => ({ ...f, patient: { ...f.patient, [k]: v } }));

  const submit = async (e) => {
    e.preventDefault();
    setErr(''); setSaving(true);
    try {
      const payload = { ...form, patient: includesPatient ? form.patient : null };
      const r = await axios.post(`${API}/incidents`, payload, { withCredentials: true });
      setDone(r.data);
    } catch (e) {
      setErr(e?.response?.data?.detail || 'Failed to submit');
    }
    setSaving(false);
  };

  if (done) {
    return (
      <div className="min-h-screen bg-background" data-testid="incidents-submitted">
        <Header />
        <div className="max-w-xl mx-auto px-4 sm:px-6 lg:px-8 py-16 text-center">
          <div className="w-20 h-20 rounded-full mx-auto flex items-center justify-center mb-6" style={{ backgroundColor: '#16A34A15' }}>
            <CheckCircle2 className="w-10 h-10" style={{ color: '#16A34A' }} />
          </div>
          <h1 className="font-['Outfit'] text-3xl font-bold text-foreground">Thank you.</h1>
          <p className="mt-2 text-sm text-muted-foreground">Your report has been recorded. The Practice Manager will review it and may be in touch if more information is needed.</p>
          <div className="mt-8 flex items-center justify-center gap-2">
            <Link to="/incidents" data-testid="incidents-done-back"
              className="text-sm font-semibold px-4 py-2 rounded-lg border border-border">Back to incidents</Link>
            <Link to="/incidents/new" data-testid="incidents-done-another"
              className="text-sm font-semibold px-4 py-2 rounded-lg text-white" style={{ backgroundColor: C.color }}>
              File another
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background" data-testid="incidents-new-page">
      <Header />
      <form onSubmit={submit} className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <button type="button" onClick={() => navigate('/incidents')}
          className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground mb-5">
          <ArrowLeft className="w-4 h-4" /> Back
        </button>

        <p className="text-[10px] font-bold uppercase tracking-[0.16em]" style={{ color: C.color }}>Incident reporting</p>
        <h1 className="font-['Outfit'] text-3xl sm:text-4xl font-bold tracking-tight text-foreground mt-1">New incident report</h1>
        <p className="mt-2 text-sm text-muted-foreground">Describe what happened in your own words. If unsure — report it. <strong>When in doubt — report it.</strong></p>

        <div className="space-y-6 mt-8 bg-card border border-border rounded-2xl p-6">
          {/* Category */}
          <div>
            <span className="text-xs font-semibold text-foreground">What type of incident?</span>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-2" data-testid="inc-categories">
              {CATEGORIES.map(c => {
                const active = form.category === c.value;
                return (
                  <button type="button" key={c.value} onClick={() => set('category', c.value)}
                    data-testid={`inc-cat-${c.value}`}
                    className="text-left text-sm py-2.5 px-3 rounded-lg border transition-colors"
                    style={active
                      ? { backgroundColor: `${C.color}15`, borderColor: C.color, color: C.color, fontWeight: 600 }
                      : { borderColor: 'hsl(var(--border))' }}>
                    {c.label}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Severity */}
          <div>
            <span className="text-xs font-semibold text-foreground">Severity</span>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-2" data-testid="inc-severities">
              {SEVERITIES.map(s => {
                const active = form.severity === s.value;
                return (
                  <button type="button" key={s.value} onClick={() => set('severity', s.value)}
                    data-testid={`inc-sev-${s.value}`}
                    className="text-left text-sm py-2.5 px-3 rounded-lg border transition-colors"
                    style={active
                      ? { backgroundColor: `${s.color}15`, borderColor: s.color, color: s.color, fontWeight: 600 }
                      : { borderColor: 'hsl(var(--border))' }}>
                    {s.label}
                  </button>
                );
              })}
            </div>
          </div>

          {/* When + where */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <label className="block">
              <span className="text-xs font-semibold text-foreground">When did it happen?</span>
              <input type="datetime-local" value={form.occurred_at}
                onChange={e => set('occurred_at', e.target.value)} required
                data-testid="inc-occurred-at"
                className="w-full mt-1 text-sm bg-background border border-border rounded-lg px-3 py-2" />
            </label>
            <label className="block">
              <span className="text-xs font-semibold text-foreground">Where?</span>
              <input type="text" value={form.location}
                onChange={e => set('location', e.target.value)}
                placeholder="e.g. CT Room, Waiting area, Reception"
                required
                data-testid="inc-location"
                className="w-full mt-1 text-sm bg-background border border-border rounded-lg px-3 py-2" />
            </label>
          </div>

          {/* Description */}
          <label className="block">
            <span className="text-xs font-semibold text-foreground">What happened?</span>
            <p className="text-[11px] text-muted-foreground">Write factually. Avoid blame. What did you see, hear or do?</p>
            <textarea value={form.description} required rows={5}
              onChange={e => set('description', e.target.value)}
              data-testid="inc-description"
              className="w-full mt-1 text-sm bg-background border border-border rounded-lg px-3 py-2" />
          </label>

          <label className="block">
            <span className="text-xs font-semibold text-foreground">What did you do immediately after?</span>
            <textarea value={form.immediate_actions} rows={3}
              onChange={e => set('immediate_actions', e.target.value)}
              data-testid="inc-immediate"
              className="w-full mt-1 text-sm bg-background border border-border rounded-lg px-3 py-2" />
          </label>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <label className="block">
              <span className="text-xs font-semibold text-foreground">Witnesses (names, if any)</span>
              <input type="text" value={form.witnesses}
                onChange={e => set('witnesses', e.target.value)}
                data-testid="inc-witnesses"
                className="w-full mt-1 text-sm bg-background border border-border rounded-lg px-3 py-2" />
            </label>
            <label className="block">
              <span className="text-xs font-semibold text-foreground">Who did you tell at the time?</span>
              <input type="text" value={form.reported_to}
                onChange={e => set('reported_to', e.target.value)}
                placeholder="e.g. Dr Chen, Practice Manager"
                data-testid="inc-reported-to"
                className="w-full mt-1 text-sm bg-background border border-border rounded-lg px-3 py-2" />
            </label>
          </div>

          {/* Patient block (optional) */}
          <div className="border-t border-border pt-4">
            <label className="flex items-center gap-2 cursor-pointer" data-testid="inc-patient-toggle">
              <input type="checkbox" checked={includesPatient}
                onChange={e => setIncludesPatient(e.target.checked)}
                className="w-4 h-4 rounded border-border" />
              <span className="text-xs font-semibold text-foreground">This incident involved a patient</span>
            </label>
            {includesPatient && (
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-3" data-testid="inc-patient-block">
                <label className="block">
                  <span className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">Patient name</span>
                  <input type="text" value={form.patient.name}
                    onChange={e => setPatient('name', e.target.value)}
                    data-testid="inc-patient-name"
                    className="w-full mt-1 text-sm bg-background border border-border rounded-lg px-3 py-2" />
                </label>
                <label className="block">
                  <span className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">DOB</span>
                  <input type="text" value={form.patient.dob}
                    onChange={e => setPatient('dob', e.target.value)}
                    data-testid="inc-patient-dob"
                    className="w-full mt-1 text-sm bg-background border border-border rounded-lg px-3 py-2" />
                </label>
                <label className="block">
                  <span className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">UR / ID</span>
                  <input type="text" value={form.patient.ur}
                    onChange={e => setPatient('ur', e.target.value)}
                    data-testid="inc-patient-ur"
                    className="w-full mt-1 text-sm bg-background border border-border rounded-lg px-3 py-2" />
                </label>
              </div>
            )}
          </div>

          {/* Anonymous toggle */}
          <div className="border-t border-border pt-4">
            <label className="flex items-start gap-2 cursor-pointer" data-testid="inc-anonymous-toggle">
              <input type="checkbox" checked={form.anonymous}
                onChange={e => set('anonymous', e.target.checked)}
                className="w-4 h-4 mt-0.5 rounded border-border" />
              <span>
                <span className="text-xs font-semibold text-foreground flex items-center gap-1">
                  {form.anonymous ? <EyeOff className="w-3 h-3" /> : <Eye className="w-3 h-3" />}
                  Submit anonymously
                </span>
                <p className="text-[11px] text-muted-foreground">Your name is hidden in the list. An encrypted audit trail is still kept, accessible only to the Practice Manager for safety/legal purposes.</p>
              </span>
            </label>
          </div>

          {err && <p className="text-xs text-red-500" data-testid="inc-error">{err}</p>}

          <div className="flex justify-end gap-2 border-t border-border pt-4">
            <button type="button" onClick={() => navigate('/incidents')}
              className="text-sm font-semibold px-4 py-2.5 rounded-lg border border-border">Cancel</button>
            <button type="submit" disabled={saving}
              data-testid="inc-submit"
              className="inline-flex items-center gap-1.5 text-sm font-semibold px-5 py-2.5 rounded-lg text-white disabled:opacity-40"
              style={{ backgroundColor: C.color }}>
              <CheckCircle2 className="w-4 h-4" /> {saving ? 'Submitting…' : 'Submit report'}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// Single-incident detail (staff view their own, PM views any)
// ────────────────────────────────────────────────────────────────────────────
export function IncidentDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isPM = user?.role === 'admin' || user?.role === 'superuser';
  const [inc, setInc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');

  // PM edit fields
  const [newStatus, setNewStatus] = useState('');
  const [statusNote, setStatusNote] = useState('');
  const [rca, setRca] = useState({ what_happened: '', why_it_happened: '', corrective_actions: '', owner: '', due_date: '' });
  const [busy, setBusy] = useState('');
  const [sopPickerOpen, setSopPickerOpen] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/incidents/${id}`, { withCredentials: true });
      setInc(r.data);
      if (r.data.rca) setRca(r.data.rca);
    } catch (e) {
      setErr(e?.response?.data?.detail || 'Not found');
    }
    setLoading(false);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  const doStatus = async () => {
    if (!newStatus) return;
    // If admin is closing and no SOPs are linked yet, gently prompt to link one
    if (newStatus === 'closed' && (inc?.linked_sops || []).length === 0) {
      const proceed = window.confirm(
        'Close without linking an SOP for early review?\n\n' +
        'Best practice: every closed incident should flag at least one SOP for review — that\'s how each incident drives a safety improvement.\n\n' +
        'Click Cancel to link an SOP first. Click OK to close anyway.'
      );
      if (!proceed) {
        setSopPickerOpen(true);
        return;
      }
    }
    setBusy('status');
    try {
      await axios.put(`${API}/incidents/${id}/status`, { status: newStatus, note: statusNote }, { withCredentials: true });
      setStatusNote(''); setNewStatus('');
      await load();
    } catch (e) { alert(e?.response?.data?.detail || 'Failed'); }
    setBusy('');
  };
  const saveRca = async () => {
    setBusy('rca');
    try {
      await axios.put(`${API}/incidents/${id}/rca`, rca, { withCredentials: true });
      await load();
    } catch (e) { alert(e?.response?.data?.detail || 'Failed'); }
    setBusy('');
  };
  const onDelete = async () => {
    if (!window.confirm('Delete this incident permanently?')) return;
    try {
      await axios.delete(`${API}/incidents/${id}`, { withCredentials: true });
      navigate('/incidents');
    } catch (e) { alert(e?.response?.data?.detail || 'Failed'); }
  };

  if (loading) return <div className="min-h-screen bg-background"><Header /><p className="p-10 text-center text-sm text-muted-foreground">Loading…</p></div>;
  if (err || !inc) return <div className="min-h-screen bg-background"><Header /><p className="p-10 text-center text-sm text-muted-foreground">{err || 'Not found'}</p></div>;

  const sv = sevMeta(inc.severity);
  const st = STATUS_META[inc.status] || STATUS_META.reported;

  return (
    <div className="min-h-screen bg-background" data-testid="incidents-detail">
      <Header />
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <button onClick={() => navigate('/incidents')} className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground mb-5">
          <ArrowLeft className="w-4 h-4" /> Back
        </button>

        {/* Header card */}
        <div className="bg-card border border-border rounded-2xl p-6 mb-6">
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div>
              <p className="text-[10px] font-bold uppercase tracking-[0.16em]" style={{ color: C.color }}>{catLabel(inc.category)}</p>
              <h1 className="font-['Outfit'] text-2xl font-bold text-foreground mt-1">{inc.location || 'Incident'}</h1>
              <p className="text-xs text-muted-foreground mt-1">
                Occurred {inc.occurred_at} · Submitted {new Date(inc.submitted_at).toLocaleString()}
                {inc.anonymous && <span className="ml-2 inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-full" style={{ backgroundColor: '#64748B15', color: '#64748B' }}><Lock className="w-2.5 h-2.5" /> Anonymous</span>}
              </p>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <span className="text-[11px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full" style={{ backgroundColor: `${sv.color}15`, color: sv.color }}>{sv.label.split(' — ')[0]}</span>
              <span className="text-[11px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full" style={{ backgroundColor: `${st.color}15`, color: st.color }}>{st.label}</span>
            </div>
          </div>

          <div className="mt-4 space-y-3">
            <Field label="What happened" value={inc.description} />
            {inc.immediate_actions && <Field label="Immediate actions" value={inc.immediate_actions} />}
            {inc.witnesses && <Field label="Witnesses" value={inc.witnesses} />}
            {inc.reported_to && <Field label="Reported to" value={inc.reported_to} />}
            {inc.patient && (inc.patient.name || inc.patient.ur) && (
              <div className="grid grid-cols-3 gap-3 p-3 bg-muted/30 rounded-xl">
                <div><p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">Patient</p><p className="text-sm font-semibold text-foreground">{inc.patient.name || '—'}</p></div>
                <div><p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">DOB</p><p className="text-sm text-foreground">{inc.patient.dob || '—'}</p></div>
                <div><p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">UR</p><p className="text-sm text-foreground">{inc.patient.ur || '—'}</p></div>
              </div>
            )}
            <div className="flex items-center gap-2 pt-2 border-t border-border">
              <User className="w-3.5 h-3.5 text-muted-foreground" />
              <p className="text-[11px] text-muted-foreground">
                Submitted by <span className="font-semibold text-foreground">{inc.submitter?.name || '—'}</span>
                {isPM && inc.submitter?.email && <span> · {inc.submitter.email}</span>}
                {inc.submitter?.role && <span> · {inc.submitter.role}</span>}
              </p>
            </div>
          </div>
        </div>

        {/* RCA (PM only or existing RCA visible to staff) */}
        {(isPM || inc.rca) && (
          <div className="bg-card border border-border rounded-2xl p-6 mb-6" data-testid="incidents-rca">
            <h2 className="font-['Outfit'] text-lg font-semibold text-foreground mb-3">Root cause analysis</h2>
            {isPM ? (
              <>
                <div className="grid grid-cols-1 gap-3">
                  <RcaField label="What happened (timeline, facts)" value={rca.what_happened}
                    onChange={v => setRca({ ...rca, what_happened: v })} testid="rca-what" />
                  <RcaField label="Why it happened (system factors — NOT who)" value={rca.why_it_happened}
                    onChange={v => setRca({ ...rca, why_it_happened: v })} testid="rca-why" />
                  <RcaField label="Corrective actions" value={rca.corrective_actions}
                    onChange={v => setRca({ ...rca, corrective_actions: v })} testid="rca-actions" />
                  <div className="grid grid-cols-2 gap-3">
                    <label className="block">
                      <span className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">Owner</span>
                      <input type="text" value={rca.owner} onChange={e => setRca({ ...rca, owner: e.target.value })}
                        data-testid="rca-owner"
                        className="w-full mt-1 text-sm bg-background border border-border rounded-lg px-3 py-2" />
                    </label>
                    <label className="block">
                      <span className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">Due date</span>
                      <input type="date" value={rca.due_date} onChange={e => setRca({ ...rca, due_date: e.target.value })}
                        data-testid="rca-due"
                        className="w-full mt-1 text-sm bg-background border border-border rounded-lg px-3 py-2" />
                    </label>
                  </div>
                </div>
                <div className="flex justify-end mt-4">
                  <button onClick={saveRca} disabled={busy === 'rca'}
                    data-testid="rca-save"
                    className="inline-flex items-center gap-1.5 text-sm font-semibold px-4 py-2 rounded-lg text-white disabled:opacity-40"
                    style={{ backgroundColor: C.color }}>
                    <CheckCircle2 className="w-4 h-4" /> Save RCA
                  </button>
                </div>
              </>
            ) : (
              <div className="space-y-2">
                <Field label="What happened" value={inc.rca.what_happened} />
                <Field label="Why it happened" value={inc.rca.why_it_happened} />
                <Field label="Corrective actions" value={inc.rca.corrective_actions} />
                {inc.rca.owner && <Field label="Owner" value={`${inc.rca.owner}${inc.rca.due_date ? ' · due ' + inc.rca.due_date : ''}`} />}
              </div>
            )}
          </div>
        )}

        {/* Read-only linked SOPs for non-PM submitters */}
        {!isPM && (inc.linked_sops || []).length > 0 && (
          <div className="bg-card border border-border rounded-2xl p-6 mb-6" data-testid="linked-sops-readonly">
            <h2 className="font-['Outfit'] text-lg font-semibold text-foreground mb-3 flex items-center gap-2">
              <Link2 className="w-4 h-4" style={{ color: C.color }} /> Linked SOPs for early review
            </h2>
            <ul className="space-y-2">
              {(inc.linked_sops || []).map(l => (
                <li key={l.sop_id} className="p-3 bg-muted/30 rounded-lg">
                  <p className="text-sm font-semibold text-foreground">{l.sop_title}</p>
                  {l.reason && <p className="text-xs text-muted-foreground mt-0.5">{l.reason}</p>}
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Linked SOPs for early review (PM only, always shown — including when empty) */}
        {isPM && (
          <LinkedSopsPanel
            incidentId={id}
            links={inc.linked_sops || []}
            onChange={load}
            openPicker={sopPickerOpen}
            setOpenPicker={setSopPickerOpen}
          />
        )}

        {/* Status updater (PM only) */}
        {isPM && (
          <div className="bg-card border border-border rounded-2xl p-6" data-testid="incidents-status-control">
            <h2 className="font-['Outfit'] text-lg font-semibold text-foreground mb-3">Update status</h2>
            <div className="flex items-center gap-2 flex-wrap">
              {Object.entries(STATUS_META).map(([val, m]) => (
                <button key={val} onClick={() => setNewStatus(val)}
                  data-testid={`status-pill-${val}`}
                  className="text-xs font-semibold px-3 py-1.5 rounded-lg border transition-colors"
                  style={newStatus === val
                    ? { backgroundColor: `${m.color}15`, borderColor: m.color, color: m.color, fontWeight: 600 }
                    : { borderColor: 'hsl(var(--border))' }}>
                  {m.label}
                </button>
              ))}
            </div>
            <textarea value={statusNote} onChange={e => setStatusNote(e.target.value)}
              rows={2} placeholder="Optional review note…"
              data-testid="status-note"
              className="w-full mt-3 text-sm bg-background border border-border rounded-lg px-3 py-2" />
            <div className="flex justify-between items-center mt-3">
              <button onClick={onDelete}
                data-testid="incident-delete"
                className="text-xs font-semibold px-3 py-1.5 rounded-lg border border-border text-red-500 hover:bg-red-500/10">
                Delete incident
              </button>
              <button onClick={doStatus} disabled={!newStatus || busy === 'status'}
                data-testid="status-save"
                className="inline-flex items-center gap-1.5 text-sm font-semibold px-4 py-2 rounded-lg text-white disabled:opacity-40"
                style={{ backgroundColor: C.color }}>
                <CheckCircle2 className="w-4 h-4" /> Save status
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function Field({ label, value }) {
  return (
    <div>
      <p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">{label}</p>
      <p className="text-sm text-foreground whitespace-pre-wrap">{value || '—'}</p>
    </div>
  );
}
function RcaField({ label, value, onChange, testid }) {
  return (
    <label className="block">
      <span className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">{label}</span>
      <textarea value={value || ''} onChange={e => onChange(e.target.value)} rows={3}
        data-testid={testid}
        className="w-full mt-1 text-sm bg-background border border-border rounded-lg px-3 py-2" />
    </label>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// Culture poster — print-optimized, for physical wall display
// ────────────────────────────────────────────────────────────────────────────
export function NearMissPosterPage() {
  const print = () => window.print();
  return (
    <div className="min-h-screen bg-background" data-testid="culture-poster">
      <div className="print:hidden"><Header /></div>

      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex items-center justify-between mb-6 print:hidden">
          <Link to="/incidents" className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground">
            <ArrowLeft className="w-4 h-4" /> Back
          </Link>
          <button onClick={print} data-testid="poster-print"
            className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-semibold text-white rounded-xl"
            style={{ backgroundColor: C.color }}>
            <Printer className="w-4 h-4" /> Print poster
          </button>
        </div>

        <div className="p-10 rounded-3xl print:rounded-none print:p-8 print:bg-white border-4" style={{ borderColor: '#1f4a2e', backgroundColor: '#f0f8fb' }}>
          <div className="text-center mb-8">
            <img src="/brand/sp-mark.png" alt="" className="h-16 w-auto mx-auto mb-4" />
            <div className="inline-flex items-center gap-2 text-[10px] font-bold tracking-[0.3em] uppercase px-3 py-1 rounded-full border-2" style={{ borderColor: '#1f4a2e', color: '#1f4a2e' }}>
              <Shield className="w-3 h-3" /> Speak-up culture
            </div>
            <h1 className="text-5xl sm:text-6xl font-black tracking-tight mt-6 leading-tight print:text-black"
              style={{ fontFamily: "'Museo Slab','Zilla Slab','Outfit',serif", fontWeight: 400, color: '#1f4a2e' }}>
              See something.<br/>
              <span style={{ color: '#3fa758' }}>Say something.</span>
            </h1>
            <p className="mt-4 text-lg italic print:text-gray-700"
              style={{ fontFamily: "'Museo Slab','Zilla Slab','Outfit',serif", fontWeight: 300, color: '#1f4a2e' }}>
              At SP Radiology, <strong style={{ fontStyle: 'normal' }}>near misses are reported</strong>, reviewed, and used to make us safer.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 my-8">
            <PosterCard num="1" title="Report it" body="Any event — near miss, patient safety, equipment, complaint. If in doubt, report it." />
            <PosterCard num="2" title="No blame" body="Reporting is encouraged and protected. You will not be penalised for raising concerns." />
            <PosterCard num="3" title="Learn from it" body="Every report is reviewed and used to improve our systems, training, and environment." />
          </div>

          <div className="my-8 p-5 rounded-2xl border-2" style={{ borderColor: '#3fa758', backgroundColor: 'white' }}>
            <p className="text-sm font-bold uppercase tracking-wider mb-2" style={{ color: '#1f4a2e' }}>How to report</p>
            <p className="text-base print:text-black" style={{ color: '#1f4a2e' }}>
              Open the SP app → <strong>Compliance</strong> → <strong>Report an incident</strong>.<br/>
              Or speak with the Practice Manager. Anonymous submissions are accepted.
            </p>
          </div>

          <div className="text-center pt-6" style={{ borderTop: '1px solid #3fa758' }}>
            <p className="text-xs italic print:text-gray-600" style={{ color: '#1f4a2e', fontFamily: "'Museo Slab','Zilla Slab','Outfit',serif", fontWeight: 300 }}>
              "No urgent or critical finding leaves the practice without confirmed communication. No incident goes unreported."
            </p>
            <p className="mt-3 text-[10px] font-bold tracking-[0.25em] uppercase" style={{ color: '#3fa758' }}>
              SP Radiology · Sanctuary Point · Ultrasound · Radiology
            </p>
          </div>
        </div>
      </div>

      <style>{`
        @media print {
          body { background: white; }
          @page { size: A3; margin: 12mm; }
        }
      `}</style>
    </div>
  );
}

function PosterCard({ num, title, body }) {
  return (
    <div className="p-5 rounded-2xl border-2 text-center" style={{ borderColor: '#3fa758', backgroundColor: 'white' }}>
      <div className="w-10 h-10 rounded-full mx-auto flex items-center justify-center text-lg font-bold text-white mb-3" style={{ backgroundColor: '#1f4a2e' }}>
        {num}
      </div>
      <p className="text-lg font-bold print:text-black" style={{ color: '#1f4a2e', fontFamily: "'Museo Slab','Zilla Slab','Outfit',serif", fontWeight: 400 }}>{title}</p>
      <p className="text-sm print:text-gray-700 mt-1" style={{ color: '#1f4a2e' }}>{body}</p>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// Linked SOPs panel — lives inside the incident detail page (admin view).
// Each link flags the SOP for early governance review so an incident becomes
// a trigger to update the SOP, not just an audit entry.
// ────────────────────────────────────────────────────────────────────────────
function LinkedSopsPanel({ incidentId, links, onChange, openPicker, setOpenPicker }) {
  return (
    <div className="bg-card border border-border rounded-2xl p-6 mb-6" data-testid="linked-sops">
      <div className="flex items-center justify-between gap-3 mb-3">
        <div>
          <h2 className="font-['Outfit'] text-lg font-semibold text-foreground flex items-center gap-2">
            <Link2 className="w-4 h-4" style={{ color: C.color }} /> Linked SOPs for early review
          </h2>
          <p className="text-xs text-muted-foreground mt-0.5">
            Every closed incident should flag at least one SOP. This turns the report into a concrete safety improvement — staff see the SOP is under review.
          </p>
        </div>
        <button onClick={() => setOpenPicker(true)}
          data-testid="linked-sops-add"
          className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-lg border shrink-0"
          style={{ borderColor: `${C.color}40`, color: C.color }}>
          <Plus className="w-3 h-3" /> Link SOP
        </button>
      </div>

      {links.length === 0 ? (
        <div className="p-6 text-center border border-dashed border-border rounded-xl" data-testid="linked-sops-empty">
          <p className="text-xs text-muted-foreground">No SOPs linked yet. Click <strong>Link SOP</strong> to pick one — we'll suggest the most relevant ones based on the incident category.</p>
        </div>
      ) : (
        <ul className="space-y-2" data-testid="linked-sops-list">
          {links.map(l => (
            <li key={l.sop_id} className="flex items-start justify-between gap-3 p-3 border border-border rounded-lg"
              data-testid={`linked-sop-${l.sop_id}`}>
              <div className="min-w-0">
                <p className="text-sm font-semibold text-foreground">{l.sop_title}</p>
                {l.reason && <p className="text-xs text-muted-foreground mt-0.5">{l.reason}</p>}
                <p className="text-[10px] text-muted-foreground mt-1">
                  Flagged for early review by {l.linked_by} · {new Date(l.linked_at).toLocaleDateString()}
                </p>
              </div>
              <button onClick={async () => {
                if (!window.confirm(`Unlink "${l.sop_title}"?`)) return;
                await axios.delete(`${API}/incidents/${incidentId}/link-sop/${l.sop_id}`, { withCredentials: true });
                onChange();
              }}
                data-testid={`linked-sop-unlink-${l.sop_id}`}
                title="Unlink"
                className="shrink-0 p-2 rounded-lg text-muted-foreground hover:text-red-500 hover:bg-accent">
                <Trash2 className="w-4 h-4" />
              </button>
            </li>
          ))}
        </ul>
      )}

      {openPicker && (
        <LinkSopPicker
          incidentId={incidentId}
          onClose={() => setOpenPicker(false)}
          onLinked={() => { setOpenPicker(false); onChange(); }}
        />
      )}
    </div>
  );
}

function LinkSopPicker({ incidentId, onClose, onLinked }) {
  const [suggestions, setSuggestions] = useState([]);
  const [allSops, setAllSops] = useState([]);
  const [q, setQ] = useState('');
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState('');

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/incidents/${incidentId}/sop-suggestions`, { withCredentials: true }).then(r => r.data).catch(() => []),
      axios.get(`${API}/sops`, { withCredentials: true }).then(r => r.data).catch(() => []),
    ]).then(([sg, sops]) => {
      setSuggestions(sg);
      setAllSops(sops.map(s => ({ sop_id: s.sop_id, title: s.title, category: s.category, version: s.version })));
    });
  }, [incidentId]);

  const link = async (sopId, title) => {
    setSaving(sopId);
    try {
      await axios.post(`${API}/incidents/${incidentId}/link-sop`,
        { sop_id: sopId, reason: reason.trim() || `Flagged from incident review — ${title}` },
        { withCredentials: true });
      onLinked();
    } catch (e) { alert(e?.response?.data?.detail || 'Link failed'); setSaving(''); }
  };

  const suggestIds = new Set(suggestions.map(s => s.sop_id));
  const ql = q.trim().toLowerCase();
  const filtered = allSops.filter(s =>
    !suggestIds.has(s.sop_id) &&
    (!ql || `${s.title} ${s.category}`.toLowerCase().includes(ql))
  );

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div onClick={e => e.stopPropagation()} data-testid="link-sop-modal"
        className="bg-card border border-border rounded-2xl max-w-2xl w-full p-5 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-['Outfit'] text-lg font-bold text-foreground">Link SOP for early review</h3>
          <button onClick={onClose} className="p-1 text-muted-foreground hover:text-foreground"><X className="w-4 h-4" /></button>
        </div>

        <label className="block mb-4">
          <span className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">Reason (optional — shown on SOP review card)</span>
          <input type="text" value={reason} onChange={e => setReason(e.target.value)}
            placeholder="e.g. Near-miss on contrast preparation — confirm checklist step"
            data-testid="link-sop-reason"
            className="w-full mt-1 text-sm bg-background border border-border rounded-lg px-3 py-2" />
        </label>

        {/* Suggested */}
        {suggestions.length > 0 && (
          <div className="mb-4" data-testid="link-sop-suggestions">
            <p className="text-[10px] font-bold uppercase tracking-wider mb-2" style={{ color: C.color }}>
              Suggested from incident category
            </p>
            <ul className="space-y-1.5">
              {suggestions.map(s => (
                <li key={s.sop_id}>
                  <button onClick={() => link(s.sop_id, s.title)}
                    disabled={!!saving}
                    data-testid={`link-sop-suggest-${s.sop_id}`}
                    className="w-full text-left flex items-center justify-between gap-3 p-3 border rounded-lg hover:bg-accent/40 transition-colors disabled:opacity-50"
                    style={{ borderColor: `${C.color}40`, backgroundColor: `${C.color}08` }}>
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-foreground truncate">{s.title}</p>
                      <p className="text-[11px] text-muted-foreground">{s.category} · v{s.version}</p>
                    </div>
                    <span className="shrink-0 text-[10px] font-bold px-2 py-0.5 rounded-full"
                      style={{ backgroundColor: `${C.color}15`, color: C.color }}>
                      {saving === s.sop_id ? 'Linking…' : 'Link'}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Full list */}
        <div>
          <div className="flex items-center gap-2 mb-2 border-b border-border pb-2">
            <Search className="w-4 h-4 text-muted-foreground" />
            <input type="search" value={q} onChange={e => setQ(e.target.value)}
              placeholder="Search all SOPs…"
              data-testid="link-sop-search"
              className="flex-1 bg-transparent border-0 outline-none text-sm" />
          </div>
          <ul className="space-y-1 max-h-60 overflow-y-auto" data-testid="link-sop-all-list">
            {filtered.length === 0 ? (
              <li className="p-4 text-center text-xs text-muted-foreground">No other SOPs match.</li>
            ) : filtered.map(s => (
              <li key={s.sop_id}>
                <button onClick={() => link(s.sop_id, s.title)}
                  disabled={!!saving}
                  data-testid={`link-sop-all-${s.sop_id}`}
                  className="w-full text-left flex items-center justify-between gap-3 px-3 py-2 rounded-lg hover:bg-accent/40 disabled:opacity-50">
                  <div className="min-w-0">
                    <p className="text-sm text-foreground truncate">{s.title}</p>
                    <p className="text-[10px] text-muted-foreground">{s.category}</p>
                  </div>
                  <span className="text-[10px] font-semibold text-muted-foreground">{saving === s.sop_id ? 'Linking…' : 'Link'}</span>
                </button>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
}

